import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import FaqFeedbackSummary from "@/pages/admin/help/components/FaqFeedbackSummary";
import FaqOptimizeSettings from "@/pages/admin/help/components/FaqOptimizeSettings";
import FaqOptimizationLedger from "@/pages/admin/help/components/FaqOptimizationLedger";
import FaqResolveModal from "@/pages/admin/help/components/FaqResolveModal";
import { useFaqFeedback } from "@/pages/admin/help/hooks/useFaqFeedback";
import { useFaqOptimization } from "@/pages/admin/help/hooks/useFaqOptimization";
import { useFaqSettings } from "@/pages/admin/help/hooks/useFaqSettings";
import { useFaqContent } from "@/pages/admin/help/hooks/useFaqContent";
import { useProfile } from "@/hooks/useProfile";
import { FAQ_ITEMS, type FaqItem } from "@/pages/help/faqData";
import type { FaqContentPayload, FaqVersionRow } from "@/pages/help/faqContent";
import { buildFaqStats } from "@/pages/admin/help/faqStats";
import { formatNumber } from "@/lib/format";

export default function HelpFeedbackPage() {
  const { t } = useTranslation();
  const { rows, loading, loadError, reload } = useFaqFeedback();
  const { rows: optimizations, savingKey, saveOptimization } = useFaqOptimization();
  const {
    rateThreshold,
    minNotHelpful,
    saving: settingsSaving,
    save: saveSettings,
  } = useFaqSettings();
  const {
    overrides,
    savingKey: savingContentKey,
    saveDraft,
    publishContent,
    publishAllDrafts,
    resetContent,
    fetchVersions,
    rollbackTo,
  } = useFaqContent();
  const { profile } = useProfile();
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);

  const editorName = profile?.full_name || profile?.email || "";
  const knownKeys = new Set(FAQ_ITEMS.map((item) => item.qKey));
  const questionLabel = (qKey: string) => (knownKeys.has(qKey) ? t(qKey) : t("helpFeedback.unknownQuestion"));

  const faqStats = useMemo(
    () => buildFaqStats(rows, optimizations, overrides, rateThreshold, minNotHelpful),
    [rows, optimizations, overrides, rateThreshold, minNotHelpful],
  );

  const total = rows.length;
  const helpful = rows.filter((row) => row.helpful).length;
  const notHelpful = total - helpful;
  const rate = total > 0 ? Math.round((notHelpful / total) * 100) : 0;

  const stats = [
    { label: t("helpFeedback.statFeedback"), value: formatNumber(total), icon: "ri-feedback-line", tone: "bg-primary-100 text-primary-700" },
    { label: t("helpFeedback.statHelpful"), value: formatNumber(helpful), icon: "ri-thumb-up-line", tone: "bg-primary-100 text-primary-700" },
    { label: t("helpFeedback.statNotHelpful"), value: formatNumber(notHelpful), icon: "ri-thumb-down-line", tone: "bg-accent-100 text-accent-700" },
    { label: t("helpFeedback.statRate"), value: `${rate}%`, icon: "ri-percent-line", tone: "bg-secondary-100 text-secondary-900" },
  ];

  const handleMarkResolved = (qKey: string, note: string, owner: string) => {
    void saveOptimization(qKey, {
      resolved: true,
      note: note || null,
      owner: owner || null,
      resolved_by: editorName || null,
    });
  };

  const handleUndoResolved = (qKey: string) => {
    void saveOptimization(qKey, { resolved: false });
  };

  const handleSaveDraft = (item: FaqItem, payload: FaqContentPayload) => {
    void saveDraft(item, payload, editorName);
  };

  const handlePublish = (item: FaqItem, payload: FaqContentPayload) => {
    void publishContent(item, payload, editorName);
  };

  const handleResetContent = (item: FaqItem) => {
    void resetContent(item, editorName);
  };

  const handleBatchPublish = async (items: FaqItem[]) => {
    await publishAllDrafts(items, editorName);
  };

  const handleRollback = (item: FaqItem, version: FaqVersionRow) => {
    void rollbackTo(item, version, editorName);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("helpFeedback.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("helpFeedback.breadcrumbAdmin")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("helpFeedback.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-questionnaire-line" />
                  </span>
                  {t("helpFeedback.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("helpFeedback.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("helpFeedback.subtitle")}
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                {t("helpFeedback.backToAdmin")}
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {loadError ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{t("helpFeedback.loadFailed")}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("helpFeedback.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-md ${item.tone}`}>
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <p className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : item.value}
                    </p>
                    <p className="mt-1 text-sm text-foreground-600">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 space-y-6">
                <FaqOptimizeSettings
                  rateThreshold={rateThreshold}
                  minNotHelpful={minNotHelpful}
                  saving={settingsSaving}
                  onSave={(nextRate, nextMin) => {
                    void saveSettings(nextRate, nextMin, editorName);
                  }}
                />

                <FaqFeedbackSummary
                  rows={rows}
                  loading={loading}
                  contentOverrides={overrides}
                  savingContentKey={savingContentKey}
                  onSaveDraft={handleSaveDraft}
                  onPublish={handlePublish}
                  onReset={handleResetContent}
                  fetchVersions={fetchVersions}
                  onRollback={handleRollback}
                  onBatchPublish={handleBatchPublish}
                  optimizations={optimizations}
                  savingKey={savingKey}
                  onResolve={(qKey) => setResolvingKey(qKey)}
                  onUndoResolved={handleUndoResolved}
                  rateThreshold={rateThreshold}
                  minNotHelpful={minNotHelpful}
                />

                <FaqOptimizationLedger
                  rows={optimizations}
                  stats={faqStats}
                  savingKey={savingKey}
                  onEditNote={(qKey) => setResolvingKey(qKey)}
                  onUndo={handleUndoResolved}
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-secondary-50 px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-lightbulb-line text-lg" />
                </span>
                <p className="text-xs leading-relaxed text-foreground-700">{t("helpFeedback.tip")}</p>
              </div>

              {resolvingKey && (
                <FaqResolveModal
                  question={questionLabel(resolvingKey)}
                  initialNote={optimizations[resolvingKey]?.note ?? ""}
                  initialOwner={optimizations[resolvingKey]?.owner ?? ""}
                  saving={savingKey === resolvingKey}
                  onClose={() => setResolvingKey(null)}
                  onConfirm={(note, owner) => {
                    handleMarkResolved(resolvingKey, note, owner);
                    setResolvingKey(null);
                  }}
                />
              )}
            </>
          )}
        </section>
      </main>

      <AppFooter />
    </div>
  );
}